
























import {
  ref,
  useContext,
  useRoute,
  defineComponent,
  useFetch,
  onMounted,
} from '@nuxtjs/composition-api';
import { merge } from 'lodash-es';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { usePageStore } from '~/stores/page';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { useWishlist, useApi, useUser } from '~/composables';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import type { Product } from '~/modules/catalog/product/types';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import getProductSwatchImages from '~/modules/catalog/product/queries/getProductSwatchImages.gql';
import getProductSpecifications from '~/modules/catalog/product/queries/getProductSpecifications.gql';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    InstagramFeed: () => import('~/components/InstagramFeed.vue'),
    MobileStoreBanner: () => import('~/components/MobileStoreBanner.vue'),
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue'),
    UpsellProducts: () => import('~/modules/catalog/product/components/UpsellProducts.vue'),
    CrosssellProducts: () => import('~/modules/catalog/product/components/CrosssellProducts.vue'),
    RecentlyViewedProducts: () => import('~/modules/recentlyViewedProducts/components/RecentlyViewedProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { app } = useContext();
    const { query } = useApi();
    const { addTags } = useCache();
    const { localePath } = useContext();
    const { getProductDetails, loading } = useProduct();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    const { isAuthenticated } = useUser();
    const product = ref<Product | null>(null);
    const route = useRoute();

    function  getProductBreadcrumbs(){
      const productCategories = product.value?.categories ?? [];
      return getBreadcrumbs(
        product.value,
        Array.isArray(productCategories) ? [...productCategories].pop() : null,
      ).map((breadcrumb) => ({ ...breadcrumb, link: breadcrumb.link }));
    };

    const getBaseSearchQuery = () => {
      const validOptionUids = new Set<string>();
      if (product.value?.configurable_options) {
        product.value.configurable_options.forEach((opt: any) => {
          (opt.values || []).forEach((val: any) => {
            if (val?.uid) validOptionUids.add(val.uid);
          });
        });
      }

      const queryConfigurations = Object.values(route.value.query)
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .filter((val): val is string => typeof val === 'string' && validOptionUids.has(val));

      return {
        filter: {
          sku: {
            eq: routeData.sku,
          },
        },
        configurations: queryConfigurations,
      };
    };

    // eslint-disable-next-line no-underscore-dangle
    // TODO : computed filed working so many times, changed this and breadcrumb filed. //const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);
    function rendererType(){
      return product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT;
    }

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      product.value = merge({}, product.value, result.items[0] as Product ?? null);
    };

    const fetchProductSwatchImagedata = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: getProductSwatchImages,
        queryVariables: searchQuery
      });

      product.value = merge({}, product.value, data.products?.items?.[0] as Product);
    };

    const fetchProductSpecificationData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: getProductSpecifications,
        queryVariables: searchQuery
      });
      product.value = merge({}, product.value, data.products?.items?.[0] as Product);
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);

      product.value = merge({}, product.value, data.products?.items?.[0] as Product);
    };

    useFetch(async () => {
      await fetchProductBaseData();
      await fetchProductSwatchImagedata();
      await fetchProductSpecificationData();
      await fetchProductExtendedData();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    onMounted(async () => {
      if (!product.value?.price_range) {
        await fetchProductExtendedData();
      }
      await loadWishlist();
    });

    return {
      //TODO: computed issue changed this and bread crumb computed field// renderer,
      rendererType,
      loading,
      getProductBreadcrumbs,
      product,
      fetchProduct: fetchProductExtendedData,
      isAuthenticated
    };
  },
  head() {
    return getMetaInfo(this.product);
  },
});
